import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage, conflictJobId } from '@/api/errors';
import { integrationsApi } from '@/api/queries/integrations';
import { activeSyncJobsQueryKey, syncJobsApi } from '@/api/queries/syncJobs';
import type { Segment } from '@/api/queries/workspace';
import {
  Button,
  Field,
  Icon,
  IconButton,
  ModalRoot,
  RadioGroup,
  useToast,
  type RadioOption,
} from '@/components/ui';

interface DesyncSegmentModalProps {
  open: boolean;
  onClose: () => void;
  segment: Segment;
  integrationId?: string;
}

export function DesyncSegmentModal({
  open,
  onClose,
  segment,
  integrationId,
}: DesyncSegmentModalProps) {
  const { data: integrations = [] } = integrationsApi.useList();
  const startDesync = syncJobsApi.useStartSegmentDesync(segment.id);
  const queryClient = useQueryClient();
  const toast = useToast();

  const [pickedId, setPickedId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const linkedIds = useMemo(
    () => Object.keys(segment.integrationSyncRefs ?? {}),
    [segment.integrationSyncRefs],
  );
  const options: RadioOption[] = useMemo(
    () =>
      linkedIds.map((id) => {
        const found = integrations.find((i) => i.id === id);
        return { value: id, label: found ? `${found.name} (${found.type})` : id };
      }),
    [linkedIds, integrations],
  );

  const effectiveId = integrationId || pickedId || linkedIds[0] || '';
  const selected = integrations.find((i) => i.id === effectiveId);
  const providerName = selected?.name ?? 'the mailing service';
  const isBrevo = selected?.type === 'brevo';
  const showPicker = !integrationId && options.length > 1;

  const close = () => {
    setError(null);
    setPickedId('');
    onClose();
  };

  const submit = async () => {
    setError(null);
    if (!effectiveId) {
      setError('Pick a provider to remove this segment from.');
      return;
    }
    try {
      await startDesync.mutateAsync(effectiveId);
      toast.show({
        severity: 'info',
        title: `Removing ${segment.name} from ${providerName}…`,
        description: isBrevo
          ? 'Removing contacts; orphaned contacts will be deleted.'
          : 'Removing contacts and deleting the list.',
      });
      close();
    } catch (e) {
      if (conflictJobId(e)) {
        void queryClient.invalidateQueries({ queryKey: activeSyncJobsQueryKey(segment.id) });
        toast.show({
          severity: 'info',
          title: 'Already running',
          description: apiErrorMessage(e, 'Another job is running for this provider.'),
        });
        close();
        return;
      }
      setError(apiErrorMessage(e, 'Failed to queue the removal job'));
    }
  };

  return (
    <ModalRoot open={open} onClose={close} size="md" closeOnScrim>
      <div className="ds-modal-head">
        <h2>Remove from {selected?.name ?? 'provider'}</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={close} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            <strong>{segment.name}</strong> — remove from {providerName}
          </div>

          {showPicker ? (
            <Field label="Remove from" required>
              <RadioGroup
                name="desync-segment-integration"
                value={effectiveId}
                onChange={setPickedId}
                options={options}
              />
            </Field>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
              {(selected ? `${selected.name} (${selected.type})` : options[0]?.label) ??
                'this integration'}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '10px 12px',
              background: 'var(--red-soft)',
              color: 'var(--red)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12.5,
              lineHeight: 1.45,
            }}
          >
            <Icon name="alert" size={14} style={{ marginTop: 2 }} />
            <div>
              {isBrevo ? (
                <>
                  Removes this segment&apos;s contacts from {providerName}. Contacts that
                  belong to another {providerName} list stay; contacts that belong to
                  no other list are <strong>deleted permanently</strong>. The{' '}
                  {providerName} list will be deleted and the segment disconnected. This
                  cannot be undone.
                </>
              ) : (
                <>
                  Removes this segment&apos;s contacts from {providerName} and{' '}
                  <strong>deletes the list</strong>, then disconnects the segment. This
                  cannot be undone.
                </>
              )}
            </div>
          </div>

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'var(--red-soft)',
                color: 'var(--red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
              }}
            >
              <Icon name="alert" size={14} /> {error}
            </div>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="danger"
          loading={startDesync.isPending}
          disabled={!effectiveId}
          leftIcon={!startDesync.isPending ? <Icon name="trash" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          Remove from {selected?.name ?? 'provider'}
        </Button>
      </div>
    </ModalRoot>
  );
}
